import type { Observable } from 'rxjs';
import type { NotificationService } from '../../services/notification.service'; // NotificationService;
import { sanitizeErrorMessage } from './error.util';

export interface ApiResponseWithData<T = any> {
  status?: boolean;
  data?: T;
  message?: string;
}

export interface SubscribeWithStatusAndDataOptions<T> {
  /** Optional success toast (e.g. "Vehicle added successfully!") */
  successToast?: {
    severity: 'success' | 'info' | 'warn' | 'error';
    summary: string;
    detail: string;
  };
  /** Error message shown when status is false or request fails */
  errorDetail: string;
  /** Error summary (default 'Error') */
  errorSummary?: string;
  /** Called when response.status && response.data */
  onSuccess: (data: T) => void;
  /** Called when response.status is false (optional) */
  onFailure?: () => void;
  /** Called when subscribe errors (optional) */
  onError?: () => void;
  /** Optional log label for console.error */
  logLabel?: string;
}

/**
 * Subscribe to an API observable, check response.status && response.data,
 * show success/error toasts and run optional callbacks. Shared by schedule-appointment
 * and appointment-details modals to avoid duplicated subscribe/next/error patterns.
 */
export function subscribeWithStatusAndData<T>(
  source: Observable<ApiResponseWithData<T>>,
  messageService: NotificationService,
  options: SubscribeWithStatusAndDataOptions<T>
): void {
  const {
    errorDetail,
    successToast,
    onSuccess,
    onFailure,
    onError,
    logLabel = 'Request',
    errorSummary = 'Error',
  } = options;
  source.subscribe({
    next: response => {
      if (response.status && response.data !== undefined && response.data !== null) {
        if (successToast) {
          messageService.add(successToast);
        }
        onSuccess(response.data as T);
      } else {
        messageService.add({
          severity: 'error',
          summary: errorSummary,
          detail: sanitizeErrorMessage(response.message || errorDetail),
        });
        onFailure?.();
      }
    },
    error: error => {
      console.error(logLabel, error);
      const detail = error?.error?.message ?? error?.message ?? errorDetail;
      messageService.add({
        severity: 'error',
        summary: errorSummary,
        detail: sanitizeErrorMessage(detail),
      });
      onError?.();
    },
  });
}
